import { useCallback, useEffect, useState } from "react";
import { useVaultWarning } from "@/context/VaultWarningContext";

const STORAGE_KEY = "exechangar:pulseColors";

export const PULSE_COLOR_PALETTE = [
  { name: "Red",    rgb: "239, 68, 68"   },
  { name: "Orange", rgb: "249, 115, 22"  },
  { name: "Yellow", rgb: "234, 179, 8"   },
  { name: "Cyan",   rgb: "34, 211, 238"  },
  { name: "Blue",   rgb: "59, 130, 246"  },
  { name: "Purple", rgb: "147, 51, 234"  },
  { name: "Pink",   rgb: "236, 72, 153"  },
  { name: "White",  rgb: "255, 255, 255" },
];

const DEFAULTS = {
  closingColor: "239, 68, 68",   // red
  openingColor: "52, 211, 153",  // green
};

// Green is not in the palette (it's the default opening color) — add it so the
// dropdown can display the current value even before the user changes anything.
export const PULSE_COLOR_PALETTE_WITH_GREEN = [
  { name: "Green",  rgb: "52, 211, 153"  },
  ...PULSE_COLOR_PALETTE,
];

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    return {
      closingColor:
        typeof parsed.closingColor === "string" && parsed.closingColor.trim()
          ? parsed.closingColor
          : DEFAULTS.closingColor,
      openingColor:
        typeof parsed.openingColor === "string" && parsed.openingColor.trim()
          ? parsed.openingColor
          : DEFAULTS.openingColor,
    };
  } catch {
    return DEFAULTS;
  }
}

function saveToStorage(colors) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(colors));
  } catch {
    // quota exceeded or private browsing — silently ignore
  }
}

export { DEFAULTS as PULSE_COLOR_DEFAULTS };

export function usePulseColors() {
  const [colors, setColors] = useState(loadFromStorage);

  const { setPulseClosingColor: setContextClosing, setPulseOpeningColor: setContextOpening } =
    useVaultWarning();

  // Persist to localStorage and sync CSS variables + context whenever colors change
  useEffect(() => {
    saveToStorage(colors);
    document.documentElement.style.setProperty(
      "--vault-pulse-closing-color",
      colors.closingColor
    );
    document.documentElement.style.setProperty(
      "--vault-pulse-opening-color",
      colors.openingColor
    );
    setContextClosing(colors.closingColor);
    setContextOpening(colors.openingColor);
  }, [colors, setContextClosing, setContextOpening]);

  const setClosingColor = useCallback((rgb) => {
    setColors((prev) => ({ ...prev, closingColor: rgb }));
  }, []);

  const setOpeningColor = useCallback((rgb) => {
    setColors((prev) => ({ ...prev, openingColor: rgb }));
  }, []);

  return {
    closingColor: colors.closingColor,
    openingColor: colors.openingColor,
    setClosingColor,
    setOpeningColor,
    colorPalette: PULSE_COLOR_PALETTE,
    colorPaletteWithGreen: PULSE_COLOR_PALETTE_WITH_GREEN,
  };
}
